<template>
    <div class="view-information-activity tw:flex tw:flex-col tw:gap-6">
        <e-field>
            <display-content :content="activityContent.content" />
        </e-field>

        <activity-completion-action-row>
            <e-tooltip
                position="is-top"
                :active="submitBlockedByExistingGrade"
                :label="translate('activity_graded_no_submissions_allowed')"
                multilined
            >
                <e-button
                    v-if="canShowSubmit"
                    :loading="isLoading"
                    :disabled="disabled"
                    type="is-primary"
                    @click="submit"
                    @keyup="submit"
                >
                    {{ translate('mark_complete') }}
                </e-button>
            </e-tooltip>
        </activity-completion-action-row>

        <submissions
            v-if="!readOnly && canViewSubmissions"
            :namespace="namespace"
            @fetch-submissions="toggleSubmissionLoading"
        />
    </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import { useActivity } from '@/composables/useActivity.js';

export default {
    name: 'ViewInformation',

    components: {
        ActivityCompletionActionRow,
        DisplayContent,
        Submissions,
    },

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        readOnly: {
            type: Boolean,
            default: false,
        },

        provideSubmissions: {
            type: Array,
            required: false,
            default: () => null,
        },
    },

    setup(props, ctx) {
        const { t: translate } = useI18n();
        const instance = getCurrentInstance();
        const api = useActivity(props, ctx, { getPayload: () => instance.proxy.payload });

        return { translate, ...api };
    },

    computed: {
        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.courseId,
                activity_id: this.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: [],
            };
        },
    },
};
</script>

<style scoped lang="scss">
@import 'resources/scss/toolbox.scss';

.view-information-activity {
    > * {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.activity-completion-action-row) {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :deep(.activity-submissions-section) {
        margin-bottom: 0 !important;
        margin-top: 0 !important;
    }
}

// Desktop/tablet (>= 769px) must render exactly as before the mobile spacing work
// (868kj17g6 AC-6, ADR-051 decision 5). The previous markup stacked Bulma `.columns`
// (1.5rem rhythm, $column-gap padding), so only the spots where that rhythm differs from
// the flat 1.5rem gap are corrected here; mobile is unchanged.
@media screen and (min-width: 769px) {
    // Last `.column` padding plus the row's own `tw:mt-4` (it out-collapses the
    // `.columns` bottom margin): $column-gap + 1rem above the action row.
    .view-information-activity > .activity-completion-action-row {
        margin-top: calc(#{$column-gap} + 1rem - 1.5rem) !important;
    }

    // The submissions `.columns` top margin cancelled its `.column` padding, so the list
    // started right under the action row.
    .view-information-activity > .activity-completion-action-row + .activity-submissions-section {
        margin-top: -1.5rem !important;
    }
}
</style>
